import { StatusCode } from '@/api/enumerations';
import { deleteCoverage } from '@/api/theHive/noticeCoverage';
import { CoverageData } from '@/api/theHive/noticeCoverage/types';
import { SelectTextField } from '@/components/CustomInput';
import { OutlinedButton } from '@/components/UI/Button';
import {
  IconDeleteMS,
  IconExpandMoreMS,
  IconSearchMS,
} from '@/constants/icons';
import { selectRegistrationUf } from '@/constants/selectOptions';
import { GlobalContext } from '@/context/global';
import { formatReal } from '@/helpers';
import useErrorMessage from '@/hooks/useErrorMessage';
import {
  Button,
  Chip,
  Collapse,
  Grid,
  InputAdornment,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';

import useCoverageView, { CoveragePole } from './hooks';
import { ModalAddingCoverage } from './ModalAddingCoverage';
import {
  CoverageContainer,
  EmptyState,
  ExpandButton,
  FiltersRow,
  MicroregionItem,
  MicroregionsBox,
  MicroregionsList,
  MicroregionsTitle,
  PanelContainer,
  PanelScrollArea,
  PoleDisplacement,
  PoleName,
  PoleRow,
  PoleRowActions,
  PoleRowHeader,
  PolesContainer,
  RowIconButton,
  SearchField,
  StateCard,
  StateChevron,
  StateHeader,
  SummaryCounts,
  SummaryInfo,
  SummaryName,
  SummaryTopLine,
  SummaryUf,
  UfFilterWrapper,
} from './styles';
import { UF_NAMES } from './ufNames';

interface CoverageAreaProps {
  data: CoverageData[];
  fetchCoverage: () => Promise<void>;
}

interface PoleItemProps {
  pole: CoveragePole;
  expanded: boolean;
  deleting: boolean;
  onExpand: (poleId: number) => void;
  onDelete: (poleId: number) => void;
}

function PoleItem({
  pole,
  expanded,
  deleting,
  onExpand,
  onDelete,
}: PoleItemProps): JSX.Element {
  const hasMicroregions = pole.microregionsCount > 0;

  return (
    <PoleRow>
      <PoleRowHeader>
        <PoleName>{pole.name}</PoleName>
        {hasMicroregions && (
          <ExpandButton
            onClick={() => onExpand(pole.id)}
            aria-label={
              expanded ? 'ocultar microrregiões' : 'mostrar microrregiões'
            }
          >
            {IconExpandMoreMS}
          </ExpandButton>
        )}
        <PoleRowActions>
          {pole.displacementFee ? (
            <PoleDisplacement>
              Deslocamento: {formatReal(pole.displacementFee)}
            </PoleDisplacement>
          ) : null}
          <Chip
            label={`${pole.microregionsCount} microrregiões`}
            size="small"
          />
          <RowIconButton
            onClick={() => onDelete(pole.id)}
            disabled={deleting}
            title="excluir polo"
          >
            {IconDeleteMS}
          </RowIconButton>
        </PoleRowActions>
      </PoleRowHeader>
      {hasMicroregions && (
        <Collapse in={expanded} unmountOnExit>
          <MicroregionsBox>
            <MicroregionsTitle>Microrregiões atendidas:</MicroregionsTitle>
            <MicroregionsList>
              {pole.microregions.map((microregion) => (
                <MicroregionItem key={microregion.id}>
                  {microregion.name}
                </MicroregionItem>
              ))}
            </MicroregionsList>
          </MicroregionsBox>
        </Collapse>
      )}
    </PoleRow>
  );
}

export function CoverageAreaSection({
  data,
  fetchCoverage,
}: CoverageAreaProps): JSX.Element {
  const [showCoverageModal, setShowCoverageModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { getErrorMessage } = useErrorMessage();
  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);

  const {
    search,
    setSearch,
    ufFilterValue,
    setUfFilterValue,
    clearFilters,
    isFilterActive,
    visibleGroups,
  } = useCoverageView(data);

  const [expandedUfs, setExpandedUfs] = useState<Set<string>>(new Set());
  const [expandedPole, setExpandedPole] = useState<number | null>(null);

  // com filtro ativo, auto-expande cada UF correspondente; recolhe tudo quando
  // o filtro é limpo. Toggles manuais não re-disparam isto (deps estáveis).
  useEffect(() => {
    setExpandedUfs(
      isFilterActive
        ? new Set(visibleGroups.map((group) => group.uf))
        : new Set()
    );
    setExpandedPole(null);
  }, [isFilterActive, visibleGroups]);

  const toggleUf = (uf: string): void => {
    setExpandedUfs((prev) => {
      const next = new Set(prev);
      if (next.has(uf)) {
        next.delete(uf);
      } else {
        next.add(uf);
      }
      return next;
    });
  };

  const handleExpand = (poleId: number): void => {
    setExpandedPole((prev) => (prev === poleId ? null : poleId));
  };

  const handleDelete = async (id: number): Promise<void> => {
    setDeletingId(id);
    try {
      const response = await deleteCoverage(id);

      if (response.detail.description)
        throw new Error(response.detail.description);
      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      fetchCoverage();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <CoverageContainer item container xs={12}>
      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography fontSize={21}>Área de cobertura</Typography>
        <Button onClick={() => setShowCoverageModal(true)}>
          adicionar cobertura
        </Button>
      </Grid>

      <Grid item xs={12} mt={2}>
        {data.length === 0 ? (
          <Typography mt={2}>Nenhuma área de cobertura adicionada</Typography>
        ) : (
          <>
            <FiltersRow>
              <SearchField
                placeholder="Buscar polo, UF ou microrregião..."
                color="secondary"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{ color: 'primary.main' }}
                    >
                      {IconSearchMS}
                    </InputAdornment>
                  ),
                }}
              />
              <UfFilterWrapper>
                <SelectTextField
                  id="uf-filter"
                  label="UF"
                  value={ufFilterValue}
                  setValue={setUfFilterValue}
                  selectOptions={selectRegistrationUf()}
                />
              </UfFilterWrapper>
              <OutlinedButton
                width="sm"
                disabled={!isFilterActive}
                onClick={clearFilters}
              >
                limpar filtros
              </OutlinedButton>
            </FiltersRow>

            <PanelContainer>
              <PanelScrollArea>
                {visibleGroups.length === 0 ? (
                  <EmptyState>
                    {isFilterActive
                      ? 'Nenhum polo encontrado com os filtros atuais.'
                      : 'Nenhum polo disponível neste edital.'}
                  </EmptyState>
                ) : (
                  visibleGroups.map((group) => {
                    const open = expandedUfs.has(group.uf);
                    return (
                      <StateCard key={group.uf}>
                        <StateHeader
                          onClick={() => toggleUf(group.uf)}
                          aria-expanded={open}
                        >
                          <SummaryUf>{group.uf}</SummaryUf>
                          <SummaryInfo>
                            <SummaryTopLine>
                              <SummaryCounts>
                                {group.total} polos · {group.microregions}{' '}
                                microrregiões
                              </SummaryCounts>
                            </SummaryTopLine>
                            <SummaryName>
                              {UF_NAMES[group.uf] ?? group.uf}
                            </SummaryName>
                          </SummaryInfo>
                          <StateChevron open={open}>
                            {IconExpandMoreMS}
                          </StateChevron>
                        </StateHeader>
                        <Collapse in={open} unmountOnExit>
                          <PolesContainer>
                            {group.poles.map((pole) => (
                              <PoleItem
                                key={pole.id}
                                pole={pole}
                                expanded={expandedPole === pole.id}
                                deleting={deletingId === pole.id}
                                onExpand={handleExpand}
                                onDelete={handleDelete}
                              />
                            ))}
                          </PolesContainer>
                        </Collapse>
                      </StateCard>
                    );
                  })
                )}
              </PanelScrollArea>
            </PanelContainer>
          </>
        )}
      </Grid>

      <ModalAddingCoverage
        isModalOpen={showCoverageModal}
        onCloseModal={() => setShowCoverageModal(false)}
        title="Área de cobertura"
        fetchCoverage={fetchCoverage}
      />
    </CoverageContainer>
  );
}
